export default function WestonDestinationsSection() {
  const stats = [
    {
      value: "55%",
      label: "of Weston requests are Latin America or Caribbean travel with under 3 weeks notice",
    },
    {
      value: "2–3 weeks",
      label: "average notice for family trips to Colombia, Brazil, or Venezuela",
    },
    {
      value: "Nov–Dec",
      label: "peak holiday travel season — passport renewals surge before winter break",
    },
    {
      value: "36-hour",
      label: "shortest turnaround we've accommodated for a Weston family emergency",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "Colombia & Venezuela",
      context: "The most frequent destinations for Weston travelers — particularly Colombian and Venezuelan families visiting relatives in Bogota, Medellin, Cali, Caracas, and Maracaibo. FLL offers direct service to Bogota and Medellin, while MIA provides additional options. These trips are often planned with short notice when family needs arise, creating urgent passport situations.",
      urgency: "Family emergencies can require travel within days — expired passports discovered during crisis planning create genuine emergencies",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Brazil",
      context: "Sao Paulo, Rio de Janeiro, and Brasilia draw Brazilian-American families from Weston for reunions, holidays, and cultural events. Brazil requires U.S. citizens to obtain a visa in advance, adding complexity to travel planning. Business travelers also fly to Sao Paulo and Curitiba for corporate meetings in finance, agriculture, and logistics.",
      urgency: "Brazilian visa processing adds 2–4 weeks to travel planning — passport validity must be confirmed before starting the visa application",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Argentina & Chile",
      context: "Buenos Aires, Mendoza, and Santiago attract Argentine-American Weston residents visiting family, as well as American travelers drawn to Patagonia, wine country, and urban culture. These southern hemisphere destinations see peak travel during U.S. winter when Argentina enjoys summer weather.",
      urgency: "Peak season (December–February) flights book months in advance — passport discoveries in November are common and costly",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "Caribbean cruises from Port Everglades and direct flights from FLL to Nassau, Jamaica, the Dominican Republic, and Puerto Rico make the Caribbean a staple for Weston family vacations. Spring break and summer cruise bookings surge every year, with many families discovering passport issues during the final booking phase.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, Los Cabos, and Mexico City are popular destinations for Weston families seeking beach vacations and cultural experiences within a short flight. Direct service from FLL and MIA makes Mexico accessible for long weekends as well as extended trips. Mexico requires six months of passport validity for entry.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March before departure",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Europe",
      context: "Paris, Rome, London, Barcelona, and Lisbon draw Weston families for summer vacations, cultural tours, and educational trips. The Schengen Area requires three months of passport validity beyond planned departure. European trips are often planned 3–6 months in advance, but actual bookings may happen closer to departure.",
      urgency: "Schengen visa requirements and six-month validity rules make expired passports a complete blocker — no exceptions at check-in",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Weston Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Weston
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Weston travelers head to Latin America, the Caribbean, Europe, and Mexico for a mix of family visits, business, cruises, and leisure. These are the destinations that create the most passport urgency for our Weston clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Fort Lauderdale", href: "/cities/fort-lauderdale/" },
              { name: "Miami", href: "/cities/miami/" },
              { name: "Boca Raton", href: "/cities/boca-raton/" },
              { name: "Delray Beach", href: "/cities/delray-beach/" },
              { name: "West Palm Beach", href: "/cities/west-palm-beach/" },
              { name: "Naples", href: "/cities/naples/" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}